import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { isCryptoAvailable } from '@/crypto';
import { transport } from '@/api/transport';
import type { NearbyCloseReason, NearbyEvent, NearbyPeer } from '@/api/types';
import { useIncomingOffer } from '@/hooks/useIncomingOffer';
import { t, tr, useT } from '@/i18n';
import { isEmbedded } from '@/lib/embed';
import { emojiName, savedNearbyName, saveNearbyName, thisDevice, type Sas } from '@/lib/nearby';
import { useAppPush } from '@/lib/useAppPush';

import { Avatar } from './Avatar';
import { IncomingDialog } from './IncomingDialog';
import { InfoTip } from './InfoTip';
import styles from './nearby.module.css';

/**
 * Nearby on the page: this device's presence among the others on the same
 * network, and the request that appears when one of them offers a bridge.
 * The provider sits at the root of the app, so a device can be reached on
 * whatever Bridge page it has open.
 */

export interface NearbyState {
  /** This device as the others see it; null until the server has answered. */
  readonly me: NearbyPeer | null;
  readonly peers: readonly NearbyPeer[];
  /** The token for this page's nearby calls. */
  readonly token: string | null;
  readonly online: boolean;
  /** Set when the network has too many devices looking already. */
  readonly full: string | null;
  /** Hears the offer steps addressed to this page as the owner (answer, accepted, closed). */
  readonly listen: (handler: (event: NearbyEvent) => void) => () => void;
}

const NearbyContext = createContext<NearbyState | null>(null);

export function useNearby(): NearbyState | null {
  return useContext(NearbyContext);
}

export function closeText(reason: NearbyCloseReason | undefined, other: string): string {
  switch (reason) {
    case 'declined':
      return t('nearby.closeDeclined', { name: other });
    case 'cancelled':
      return t('nearby.closeCancelled', { name: other });
    case 'expired':
      return t('nearby.closeExpired');
    case 'gone':
      return t('nearby.closeGone', { name: other });
    case 'view_limit':
      return t('nearby.closeViewLimit');
    default:
      return t('nearby.closeDefault');
  }
}

/**
 * A nearby device's face: the same odd little characters as the
 * chat, picked from the device's name, so both screens show the same face.
 */
export function PeerAvatar({ peer, size = 'normal' }: { readonly peer: NearbyPeer; readonly size?: 'normal' | 'large' }): React.JSX.Element {
  const frame = [styles.avatar, size === 'large' ? styles.avatarLarge : ''].join(' ');
  return <Avatar seed={`nearby:${peer.name}`} size={size === 'large' ? 56 : 32} className={[frame, styles.avatarFace].join(' ')} />;
}

export function EmojiRow({ emoji, testId }: { readonly emoji: readonly Sas[]; readonly testId?: string | undefined }): React.JSX.Element {
  return (
    <p className={styles.emoji} role="img" aria-label={emoji.map((e) => emojiName(e)).join(', ')} data-testid={testId}>
      {emoji.map((e, index) => (
        <span key={`${e.name}-${String(index)}`} aria-hidden="true">
          {e.emoji}
        </span>
      ))}
    </p>
  );
}

export function NearbyProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const navigate = useNavigate();
  const [me, setMe] = useState<NearbyPeer | null>(null);
  const [peers, setPeers] = useState<readonly NearbyPeer[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [online, setOnline] = useState(false);
  const [full, setFull] = useState<string | null>(null);
  const listeners = useRef(new Set<(event: NearbyEvent) => void>());
  const tokenRef = useRef<string | null>(null);
  const offer = useIncomingOffer(tokenRef, navigate);
  const { onEvent, onTokenChanged } = offer;

  // in the native app with notifications allowed, the presence carries the device token: offers reach it closed too
  const push = useAppPush();
  useEffect(() => {
    // a framing page must not be able to put a pairing dialog in front of the person (docs/THREAT_MODEL.md, “Frames”)
    if (!isCryptoAvailable() || isEmbedded()) return undefined;
    const leave = transport.nearby(thisDevice(), savedNearbyName(), (event) => {
      switch (event.type) {
        case 'hello':
          // a new page token after a dropped stream: what was in progress was closed on the server with the old one
          if (tokenRef.current !== null && tokenRef.current !== event.token) onTokenChanged();
          tokenRef.current = event.token;
          setToken(event.token);
          setMe(event.me);
          saveNearbyName(event.me.name);
          break;
        case 'peers':
          setPeers(event.peers);
          break;
        case 'connection':
          setOnline(event.online);
          break;
        case 'full':
          setFull(event.message);
          break;
        case 'offer':
        case 'reveal':
        case 'ready':
          onEvent(event, tokenRef.current);
          break;
        case 'closed':
          onEvent(event, tokenRef.current);
          for (const listener of listeners.current) listener(event);
          break;
        default:
          for (const listener of listeners.current) listener(event);
      }
    }, push);
    return () => {
      tokenRef.current = null;
      leave();
    };
  }, [onEvent, onTokenChanged, push]);

  const listen = useCallback((handler: (event: NearbyEvent) => void) => {
    listeners.current.add(handler);
    return () => {
      listeners.current.delete(handler);
    };
  }, []);

  const state = useMemo<NearbyState>(() => ({ me, peers, token, online, full, listen }), [me, peers, token, online, full, listen]);

  return (
    <NearbyContext.Provider value={state}>
      {children}
      {offer.incoming ? <IncomingDialog incoming={offer.incoming} me={me} onAccept={offer.accept} onDecline={offer.decline} /> : null}
    </NearbyContext.Provider>
  );
}

/** “Visible nearby as Teal Otter”: so the person knows which name to pick on the other device. */
export function NearbyBadge({ className, reserve = false }: { readonly className?: string | undefined; readonly reserve?: boolean }): React.JSX.Element | null {
  useT();
  const nearby = useNearby();
  // `reserve`: keep the line's height while the name is on its way, so nothing below or beside it jumps.
  if (!nearby?.me) return reserve ? <p className={[styles.badge, className ?? ''].join(' ')} aria-hidden="true">{'\u00a0'}</p> : null;
  return (
    <p className={[styles.badge, className ?? ''].join(' ')} data-testid="nearby-badge">
      <i aria-hidden="true" />
      <span>{tr('nearby.visibleAs', { strong: (children) => <strong>{children}</strong> }, { name: nearby.me.name })}</span>
      <InfoTip label={t('nearby.infoLabel')} testId="nearby-info">
        {tr('nearby.infoBody', { b: (children) => <b>{children}</b> }, { name: nearby.me.name })}
      </InfoTip>
    </p>
  );
}
